import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteFooter from "../../general/components/layout/SiteFooter";
import SiteNavbar from "../../general/components/layout/SiteNavbar";
import { cursos } from "../assets/InfoCursos";

const areaConfigs = {
    nutricion: {
        title: "Nutrición",
        includedPrograms: [
            "posgrado-nutricion-clinica",
            "posgrado-nutricion-microbiota",
            "diplomatura-nutricion-deportiva",
            "diplomatura-nutricion-diabetes",
            "diplomatura-nutricion-plant-based",
        ],
        themeProgram: "posgrado-nutricion-clinica",
    },
    estetica: {
        title: "Estética",
        includedPrograms: [
            "posgrado-dermatologia-estetica-terapeutica",
            "diplomatura-ginecologia-regenerativa-estetica-funcional",
            "diplomatura-bioremodelacion-facial",
            "diplomatura-microtrasplante-capilar",
            "diplomatura-skincare",
        ],
        themeProgram: "posgrado-dermatologia-estetica-terapeutica",
    },
    "ia-y-tecnologia": {
        title: "IA y Tecnología",
        includedPrograms: [
            "posgrado-bigdata-ia-salud",
            "posgrado-gestion-de-establecimientos-sistemas-de-salud-digital",
            "diplomatura-ia-medica",
            "diplomatura-gestion-telemedicina",
        ],
        themeProgram: "posgrado-bigdata-ia-salud",
    },
    "salud-mental-y-adicciones": {
        title: "Salud Mental y Adicciones",
        includedPrograms: [
            "posgrado-toxicologia",
            "posgrado-psiquiatria-clinica",
            "diplomatura-medicina-adicciones",
            "curso-acomp-terapeutico-adicciones",
        ],
        themeProgram: "posgrado-toxicologia",
    },
    enfermeria: {
        title: "Enfermería",
        includedPrograms: [
            "posgrado-enfermeria-oncologica",
            "posgrado-gestion-administracion-enfermeria",
            "diplomatura-enfermeria-neurologica",
            "diplomatura-enfermeria-consumos-problematicos",
            "curso-rcp",
            "programa-inmunizaciones",
            "curso-operador-prehospitalario-emergencias",
        ],
        themeProgram: "posgrado-gestion-administracion-enfermeria",
    },
    neurocardiologia: {
        title: "Neurocardiología",
        includedPrograms: ["diplomatura-urgencias-neurocardiologicas"],
        themeProgram: "diplomatura-urgencias-neurocardiologicas",
    },
    "medicina-laboral": {
        title: "Medicina Laboral",
        includedPrograms: ["diplomatura-medicina-laboral-energia-mineria"],
        themeProgram: "diplomatura-medicina-laboral-energia-mineria",
    },
    criminalistica: {
        title: "Criminalística",
        includedPrograms: ["diplomatura-criminalistica-ciencias-forenses"],
        themeProgram: "diplomatura-criminalistica-ciencias-forenses",
    },
    marketing: {
        title: "Marketing",
        includedPrograms: ["diplomatura-marketing-medico"],
        themeProgram: "diplomatura-marketing-medico",
    },
    "investigacion-clinica": {
        title: "Investigación Clínica",
        includedPrograms: ["posgrado-investigacion-clinica"],
        themeProgram: "posgrado-investigacion-clinica",
    },
};

const typeFilters = [
    { key: "all", label: "Todos" },
    { key: "posgrado", label: "Posgrados" },
    { key: "diplomatura", label: "Diplomaturas" },
    { key: "curso", label: "Cursos" },
];

const typeOrder = { Posgrado: 0, Diplomatura: 1, Curso: 2 };
const utf8Decoder = new TextDecoder("utf-8", { fatal: true });
const defaultTheme = {
    primary: "#31507a",
    primaryDark: "#131b2d",
    primaryLight: "#9aadd0",
    pale: "#e1e8f3",
    background: "#f7f9fc",
    navy: "#253752",
    navyDark: "#0b1120",
    heroStart: "#0b1120",
    heroMid: "#31507a",
    heroEnd: "#253752",
};

function normalizeSpanishText(value) {
    if (typeof value !== "string" || !/[ÃÂâ]/.test(value)) {
        return value || "";
    }

    let nextValue = value;

    for (let index = 0; index < 2; index += 1) {
        if (!/[ÃÂâ]/.test(nextValue)) {
            break;
        }

        try {
            nextValue = utf8Decoder.decode(Uint8Array.from(nextValue, (character) => character.charCodeAt(0)));
        } catch {
            break;
        }
    }

    return nextValue;
}

function getProgramTitle(program) {
    return normalizeSpanishText(program.nombre || "")
        .replace(/^Posgrado en\s+/i, "")
        .replace(/^Posgrado de\s+/i, "")
        .replace(/^Diplomatura en\s+/i, "")
        .replace(/^Curso de\s+/i, "");
}

function getProgramMeta(program) {
    if (program.duracion) {
        return `${program.duracion} ${program.duracion === 1 ? "mes" : "meses"}`;
    }

    if (program.clases) {
        return `${program.clases} ${program.clases === 1 ? "clase" : "clases"}`;
    }

    return normalizeSpanishText(program.modalidad) || "Inscripciones abiertas";
}

function programBelongsToArea(program, config) {
    if (config.includedPrograms) {
        return config.includedPrograms.includes(program.url);
    }

    const categories = Array.isArray(program.categoria) ? program.categoria : [];
    const isExcluded = (config.excludedPrograms || []).includes(program.url);

    return !isExcluded && config.categories.some((category) => categories.includes(category));
}

function ProgramCard({ program }) {
    const programType = (program.tipo || "").toLowerCase();
    const title = getProgramTitle(program);
    const description = normalizeSpanishText(program.descripcionPreview || program.descripcion || "").trim();

    return (
        <Link to={`/${program.routeFamily}/${program.routeSlug}`} className="c-card">
            <div className="c-card-top">
                {program.cuposLlenos ? <span className="badge badge-red">Cupos llenos</span> : null}
                {program.foto ? (
                    <img className="c-card-cover" src={program.foto} alt={title} loading="lazy" />
                ) : (
                    <div className="bg g1"></div>
                )}
            </div>
            <div className="c-card-body">
                <span className="type">{program.tipo}</span>
                <h3>{title}</h3>
                <p>{description}</p>
                <div className="c-meta">
                    <span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 6v6l4 2"></path>
                        </svg>
                        {getProgramMeta(program)}
                    </span>
                    {["posgrado", "diplomatura"].includes(programType) ? <span>Certificado FISP + UBA</span> : null}
                </div>
            </div>
        </Link>
    );
}

export default function AreaEducativa() {
    const { area } = useParams();
    const config = areaConfigs[area];
    const [activeType, setActiveType] = useState("all");

    useEffect(() => {
        setActiveType("all");
    }, [area]);

    const areaPrograms = useMemo(() => {
        if (!config) {
            return [];
        }

        return cursos
            .filter((program) => programBelongsToArea(program, config))
            .sort((leftProgram, rightProgram) => {
                const soldOutDifference = Number(Boolean(leftProgram.cuposLlenos)) - Number(Boolean(rightProgram.cuposLlenos));

                if (soldOutDifference !== 0) {
                    return soldOutDifference;
                }

                const typeDifference = (typeOrder[leftProgram.tipo] ?? 99) - (typeOrder[rightProgram.tipo] ?? 99);

                if (typeDifference !== 0) {
                    return typeDifference;
                }

                return leftProgram.nombre.localeCompare(rightProgram.nombre, "es", { sensitivity: "base" });
            });
    }, [config]);

    if (!config) {
        return <Navigate to="/educativo" replace />;
    }

    const visiblePrograms = activeType === "all"
        ? areaPrograms
        : areaPrograms.filter((program) => (program.tipo || "").toLowerCase() === activeType);
    const themeProgram = cursos.find((program) => program.url === config.themeProgram);
    const theme = themeProgram?.theme || defaultTheme;
    const areaStyles = {
        "--area-primary": theme.primary,
        "--area-primary-dark": theme.primaryDark,
        "--area-primary-light": theme.primaryLight,
        "--area-pale": theme.pale,
        "--area-background": theme.background,
        "--area-navy": theme.navy,
        "--area-navy-dark": theme.navyDark,
        "--area-hero-start": theme.heroStart,
        "--area-hero-mid": theme.heroMid,
        "--area-hero-end": theme.heroEnd,
    };

    return (
        <div className="educativo area-educativa" style={areaStyles}>
            <SiteNavbar activeSection="educativo" navTheme={theme} />

            <section className="area-hero">
                <div className="cat-inner area-hero-inner">
                    <div className="breadcrumb">
                        <Link to="/">Inicio</Link>
                        <span className="sep">→</span>
                        <Link to="/educativo">Educativo</Link>
                    </div>
                    <h1>{config.title}</h1>
                </div>
            </section>

            <main className="catalog-section area-catalog" id="programas">
                <div className="cat-inner">
                    <div className="cat-head">
                        <div>
                            <h2>Programas de {config.title}</h2>
                            {areaPrograms.length > 0 ? (
                                <p>{areaPrograms.length} {areaPrograms.length === 1 ? "programa" : "programas"}</p>
                            ) : null}
                        </div>
                    </div>

                    <div className="cat-tabs" aria-label="Filtrar programas por tipo">
                        {typeFilters.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                className={`cat-tab ${activeType === filter.key ? "active" : ""}`}
                                onClick={() => setActiveType(filter.key)}
                                aria-pressed={activeType === filter.key}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>

                    <div className="cat-grid">
                        {visiblePrograms.map((program) => <ProgramCard key={program.id} program={program} />)}
                    </div>
                </div>
            </main>

            <section className="cta area-contact-cta">
                <div className="cta-inner">
                    <h2>¿Necesitás ayuda para elegir?</h2>
                    <p>Escribinos y te orientamos sobre los programas de esta área.</p>
                    <div className="cta-btns">
                        <a href="https://wa.me/5491123879118" className="btn-wh">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            Consultá por WhatsApp
                        </a>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
